import { z } from 'zod';
import type { FormikErrors, FormikValues } from 'formik';

interface ZodToFormikValidateOptions {
  useNestedPaths?: boolean;
}

/**
 * Converts a Zod schema to a Formik validate function.
 * This allows using Zod schemas with Formik's validation system.
 *
 * @param schema - The Zod schema to validate against
 * @param options - Optional configuration
 * @param options.useNestedPaths - If true, uses dot notation for nested paths (e.g., "position.left")
 * @returns An async Formik validate function that resolves to an errors object
 *
 * @example
 * const schema = z.object({
 *   name: z.string().min(1, 'Name is required'),
 *   email: z.string().email('Invalid email'),
 * });
 *
 * <Formik
 *   validate={zodToFormikValidate(schema)}
 *   ...
 * />
 */
export function zodToFormikValidate<T extends FormikValues>(
  schema: z.ZodType<T>,
  options: ZodToFormikValidateOptions = {}
): <V extends FormikValues = T>(values: V) => Promise<FormikErrors<V>> {
  const { useNestedPaths = false } = options;

  return async <V extends FormikValues = T>(
    values: V
  ): Promise<FormikErrors<V>> => {
    try {
      await schema.parseAsync(values);
      return {};
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: FormikErrors<V> = {};
        error.issues.forEach(err => {
          if (err.path.length > 0) {
            if (useNestedPaths && err.path.length > 1) {
              // Create nested error objects for nested paths
              let current: Record<string, unknown> = errors as Record<
                string,
                unknown
              >;
              for (let i = 0; i < err.path.length - 1; i++) {
                const key = String(err.path[i]);
                if (!current[key] || typeof current[key] !== 'object') {
                  current[key] = {};
                }
                current = current[key] as Record<string, unknown>;
              }
              current[String(err.path[err.path.length - 1])] = err.message;
            } else {
              const fieldName = String(err.path[0]);
              (errors as Record<string, string>)[fieldName] = err.message;
            }
          }
        });
        return errors;
      }
      return {};
    }
  };
}
